import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

export interface AssignableLesson {
  lessonId: string
  lessonTitle: string
  courseTitle: string
}

export interface StudentAssignment {
  id: string
  lessonId: string
  lessonTitle: string
  assignedAt: string
  inCarCompletedAt: string | null
  videoUnlockedAt: string | null
}

/** Every published lesson this instructor can assign — "teaching role read" (migration
 * 038) already grants instructors unconditional read of all published curriculum. */
export function useAssignableLessons() {
  const { user } = useAuth()
  const [lessons, setLessons] = useState<AssignableLesson[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) return
    let cancelled = false
    async function load() {
      setLoading(true)
      const { data } = await supabase
        .from('curriculum_version_lessons')
        .select('id, title, curriculum_version_modules(course_version_id, curriculum_course_versions(title, status, version_number, course_id, curriculum_courses(title)))')
        .order('title', { ascending: true })
      if (cancelled) return

      const published = (data ?? []).filter((r: any) => r.curriculum_version_modules?.curriculum_course_versions?.status === 'published')

      // A course can legitimately have more than one published version at once (a
      // "replace" never archives what it supersedes, so a student already assigned
      // against the old content keeps reading it — see useFlatCurriculum.ts). For a NEW
      // assignment, only ever offer the latest: keep the highest version_number per
      // course_id.
      const latestVersionNumberByCourse = new Map<string, number>()
      for (const r of published as any[]) {
        const cv = r.curriculum_version_modules?.curriculum_course_versions
        const courseId = cv?.course_id
        const versionNumber = cv?.version_number ?? 0
        if (!courseId) continue
        const current = latestVersionNumberByCourse.get(courseId) ?? -1
        if (versionNumber > current) latestVersionNumberByCourse.set(courseId, versionNumber)
      }

      setLessons((published as any[])
        .filter(r => {
          const cv = r.curriculum_version_modules?.curriculum_course_versions
          return cv?.course_id && cv.version_number === latestVersionNumberByCourse.get(cv.course_id)
        })
        .map((r: any) => ({
          lessonId: r.id,
          lessonTitle: r.title,
          courseTitle: r.curriculum_version_modules?.curriculum_course_versions?.curriculum_courses?.title ?? 'Course',
        })))
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [user])

  return { lessons, loading }
}

/** This instructor's assignments for one specific student, plus the actions that mutate
 * them. Both RPCs (migration 066) derive authorization from the caller's own active
 * instructor_student_relationships row — never trust anything this hook asserts client-side. */
export function useStudentAssignments(studentId: string | undefined) {
  const { user } = useAuth()
  const [assignments, setAssignments] = useState<StudentAssignment[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [enrolled, setEnrolled] = useState<boolean | null>(null)

  const fetchAssignments = useCallback(async () => {
    if (!user || !studentId) return
    setLoading(true)
    const { data } = await supabase
      .from('curriculum_lesson_assignments')
      .select('id, lesson_id, assigned_at, in_car_completed_at, video_unlocked_at, curriculum_version_lessons(title)')
      .eq('instructor_id', user.id)
      .eq('student_id', studentId)
      .order('assigned_at', { ascending: false })
    setAssignments((data ?? []).map((r: any) => ({
      id: r.id, lessonId: r.lesson_id, lessonTitle: r.curriculum_version_lessons?.title ?? 'Lesson',
      assignedAt: r.assigned_at, inCarCompletedAt: r.in_car_completed_at, videoUnlockedAt: r.video_unlocked_at,
    })))

    const { count } = await supabase
      .from('student_course_enrollments')
      .select('id', { count: 'exact', head: true })
      .eq('student_id', studentId)
      .eq('status', 'active')
    setEnrolled((count ?? 0) > 0)
    setLoading(false)
  }, [user, studentId])

  useEffect(() => { fetchAssignments() }, [fetchAssignments])

  const enrollInCourse = useCallback(async (courseVersionId: string) => {
    if (!studentId) return { ok: false, error: 'no_student' }
    setSaving(true)
    try {
      const { data, error: rpcErr } = await supabase.rpc('instructor_enroll_student_in_course', {
        p_student_id: studentId, p_course_version_id: courseVersionId,
      })
      const result = data as { ok: boolean; code?: string } | null
      if (rpcErr || !result?.ok) return { ok: false, error: result?.code ?? rpcErr?.message }
      await fetchAssignments()
      return { ok: true }
    } finally {
      setSaving(false)
    }
  }, [studentId, fetchAssignments])

  const assignLesson = useCallback(async (lessonId: string) => {
    if (!studentId) return { ok: false, error: 'no_student' }
    setSaving(true)
    try {
      const { data, error: rpcErr } = await supabase.rpc('create_curriculum_lesson_assignment', {
        p_student_id: studentId, p_lesson_id: lessonId,
      })
      const result = data as { ok: boolean; code?: string } | null
      if (rpcErr || !result?.ok) return { ok: false, error: result?.code ?? rpcErr?.message }
      await fetchAssignments()
      return { ok: true }
    } finally {
      setSaving(false)
    }
  }, [studentId, fetchAssignments])

  const markInCarComplete = useCallback(async (assignmentId: string) => {
    setSaving(true)
    try {
      const { data, error: rpcErr } = await supabase.rpc('mark_lesson_assignment_in_car_complete', {
        p_assignment_id: assignmentId,
      })
      const result = data as { ok: boolean; code?: string } | null
      if (rpcErr || !result?.ok) return { ok: false, error: result?.code ?? rpcErr?.message }
      await fetchAssignments()
      return { ok: true }
    } finally {
      setSaving(false)
    }
  }, [fetchAssignments])

  return { assignments, loading, saving, enrolled, refresh: fetchAssignments, enrollInCourse, assignLesson, markInCarComplete }
}

/** Published course versions, for the "enroll in which course" picker. */
export function usePublishedCourseVersions() {
  const { user } = useAuth()
  const [versions, setVersions] = useState<{ id: string; title: string; courseTitle: string }[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) return
    let cancelled = false
    async function load() {
      setLoading(true)
      const { data } = await supabase
        .from('curriculum_course_versions')
        .select('id, title, status, version_number, course_id, curriculum_courses(title)')
        .eq('status', 'published')
      if (cancelled) return

      // Same latest-version-per-course dedup as useAssignableLessons above, and for the
      // same reason — a course can have more than one published version at once.
      const latestVersionNumberByCourse = new Map<string, number>()
      for (const r of (data ?? []) as any[]) {
        const current = latestVersionNumberByCourse.get(r.course_id) ?? -1
        if (r.version_number > current) latestVersionNumberByCourse.set(r.course_id, r.version_number)
      }

      setVersions((data ?? [])
        .filter((r: any) => r.version_number === latestVersionNumberByCourse.get(r.course_id))
        .map((r: any) => ({ id: r.id, title: r.title, courseTitle: r.curriculum_courses?.title ?? 'Course' })))
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [user])

  return { versions, loading }
}
